/* CompareRecoveryWidget — Admin Overview tile.
 *
 * Funnel for the 24h compare-cart recovery drip:
 *   snapshots queued → emails sent → opens → clicks → attributed orders
 *
 * Mirrors the time-window pattern of SearchAnalyticsWidget (7/30/90 days).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Mail, RefreshCw, Send, Eye, MousePointerClick, ShoppingCart, AlertTriangle, Sparkles,
} from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

function fmtMoney(v) {
  const n = Number(v || 0);
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const STATUS_META = {
  pending:                   { label: 'Pending',         color: '#a8871a', bg: '#fffbe9' },
  sent:                      { label: 'Sent',            color: '#15803d', bg: '#dcfce7' },
  send_failed:               { label: 'Send failed',     color: '#b91c1c', bg: '#fee2e2' },
  suppressed_unsub:          { label: 'Unsubscribed',    color: '#475569', bg: '#f1f5f9' },
  suppressed_purchased:      { label: 'Purchased',       color: '#15803d', bg: '#dcfce7' },
  suppressed_freq_cap:       { label: 'Freq cap',        color: '#475569', bg: '#f1f5f9' },
  suppressed_dupe:           { label: 'Dupe set',        color: '#475569', bg: '#f1f5f9' },
  suppressed_invalid_email:  { label: 'Invalid email',   color: '#b91c1c', bg: '#fee2e2' },
};

export default function CompareRecoveryWidget() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState({
    snapshots_queued: 0,
    emails_sent: 0,
    emails_opened: 0,
    click_count: 0,
    snapshots_with_click: 0,
    attributed_orders: 0,
    attributed_revenue: 0,
    open_rate: 0,
    click_rate: 0,
    conversion_rate: 0,
    status_breakdown: {},
    recent: [],
  });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = {};
      try {
        const pw = localStorage.getItem('zhelta_admin_pw');
        if (pw) headers['X-Admin-Password'] = pw;
      } catch { /* ignore */ }
      const r = await fetch(
        `${API}/admin/compare/recovery-stats?days=${days}`,
        { credentials: 'include', headers },
      );
      if (r.ok) setData(await r.json());
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const empty = !loading && data.snapshots_queued === 0;

  return (
    <section
      data-testid="admin-compare-recovery-widget"
      className="rounded-2xl border border-gray-200 bg-white overflow-hidden"
    >
      <header className="px-6 py-4 flex items-center justify-between bg-[#f8f8fa] border-b border-gray-100 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#c9a227]" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#a8871a]">Compare → Email recovery</div>
            <h3 className="text-[15px] font-bold text-gray-900">Drip funnel — last {days} days</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              data-testid={`compare-recovery-period-${d}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                days === d ? 'bg-[#c9a227] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#c9a227]'
              }`}
            >
              {d}d
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            data-testid="compare-recovery-refresh"
            disabled={loading}
            className="p-1.5 rounded-full border border-gray-200 text-gray-500 hover:text-[#c9a227] hover:border-[#c9a227] transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      <div className="px-6 py-3 grid grid-cols-2 md:grid-cols-5 gap-3 border-b border-gray-100">
        <FunnelStat icon={Sparkles}       label="Queued"      value={data.snapshots_queued} testid="compare-recovery-stat-queued" />
        <FunnelStat icon={Send}           label="Emails sent" value={data.emails_sent}      testid="compare-recovery-stat-sent" />
        <FunnelStat icon={Eye}            label={`Opens · ${data.open_rate}%`}     value={data.emails_opened}        testid="compare-recovery-stat-opens" />
        <FunnelStat icon={MousePointerClick} label={`Clicks · ${data.click_rate}%`} value={data.snapshots_with_click} testid="compare-recovery-stat-clicks" />
        <FunnelStat icon={ShoppingCart}   label={`Conv · ${data.conversion_rate}%`} value={data.attributed_orders}    testid="compare-recovery-stat-conv" highlight />
      </div>

      <div className="px-6 py-3 flex items-center justify-between gap-3 flex-wrap border-b border-gray-100">
        <span className="text-[12px] text-gray-600">
          Attributed revenue: <strong className="text-[#a8871a]" data-testid="compare-recovery-revenue">{fmtMoney(data.attributed_revenue)}</strong>
          <span className="text-gray-400 ml-2">· orders placed within the email-sent → now window</span>
        </span>
        {Object.keys(data.status_breakdown || {}).length > 0 && (
          <span className="flex items-center gap-1.5 flex-wrap">
            {Object.entries(data.status_breakdown).map(([status, count]) => {
              const meta = STATUS_META[status] || { label: status, color: '#525252', bg: '#f4f4f5' };
              return (
                <span
                  key={status}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold"
                  style={{ background: meta.bg, color: meta.color }}
                  data-testid={`compare-recovery-status-${status}`}
                >
                  {meta.label} · {count}
                </span>
              );
            })}
          </span>
        )}
      </div>

      {empty ? (
        <div className="text-center py-10 px-6" data-testid="compare-recovery-empty">
          <Mail className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-[13px] text-gray-500">No compare recovery snapshots in this window yet.</p>
          <p className="text-[11.5px] text-gray-400 mt-1">As visitors compare products and leave their email, drip activity will land here.</p>
        </div>
      ) : (
        <div className="px-6 py-4" data-testid="compare-recovery-recent">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-gray-500 mb-2.5">Recent snapshots</div>
          <ul className="flex flex-col gap-1.5">
            {data.recent.map((r) => {
              const meta = STATUS_META[r.status] || { label: r.status, color: '#525252', bg: '#f4f4f5' };
              return (
                <li
                  key={r.id}
                  data-testid={`compare-recovery-row-${r.id}`}
                  className="flex items-center gap-3 py-1.5 px-2 rounded-md hover:bg-gray-50 transition-colors text-[12px]"
                >
                  <span className="font-semibold text-gray-900 truncate max-w-[220px]">{r.email}</span>
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                    style={{ background: meta.bg, color: meta.color }}
                  >
                    {meta.label}
                  </span>
                  <span className="text-gray-500">{r.product_count} product{r.product_count === 1 ? '' : 's'}</span>
                  {r.click_count > 0 && (
                    <span className="text-emerald-600 font-bold inline-flex items-center gap-1">
                      <MousePointerClick className="w-3 h-3" /> {r.click_count}
                    </span>
                  )}
                  {r.email_opened_at && (
                    <span className="text-blue-600 inline-flex items-center gap-1" title={`Opened ${timeAgo(r.email_opened_at)}`}>
                      <Eye className="w-3 h-3" />
                    </span>
                  )}
                  <span className="text-gray-400 ml-auto">{timeAgo(r.email_sent_at || r.created_at)}</span>
                </li>
              );
            })}
          </ul>
          {data.send_failed > 0 && (
            <div className="mt-3 flex items-center gap-2 text-[11.5px] text-rose-700 bg-rose-50 px-3 py-2 rounded-md">
              <AlertTriangle className="w-3.5 h-3.5" />
              {data.send_failed} email{data.send_failed === 1 ? '' : 's'} failed to send — check Resend domain verification.
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function FunnelStat({ icon: Icon, label, value, highlight = false, testid }) {
  return (
    <div
      data-testid={testid}
      className={`flex items-start gap-2 ${highlight ? 'bg-[#fffbe9] border border-[#f0e3a8] rounded-md p-2' : ''}`}
    >
      <span className={`h-8 w-8 rounded-md grid place-items-center shrink-0 ${highlight ? 'bg-[#c9a227] text-white' : 'bg-[#c9a227]/10 text-[#c9a227]'}`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <span className="flex flex-col leading-tight">
        <span className="text-[16px] font-bold text-gray-900">{value}</span>
        <span className="text-[10.5px] uppercase tracking-wider text-gray-500">{label}</span>
      </span>
    </div>
  );
}
